import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import FloatingBackground from '../../Common/FloatingBackground/FloatingBackground';
import './ContactModal.css';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    first: '',
    last: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY',
          subject: `[Website] ${form.subject || 'New Inquiry'}`,
          from_name: `${form.first} ${form.last}`,
          from_email: form.email,
          name: `${form.first} ${form.last}`,
          email: form.email,
          phone: form.phone,
          message: `Subject: ${form.subject || 'No subject'}\n\nPhone: ${form.phone || 'Not provided'}\n\nMessage:\n${form.message}`,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you! Your message has been sent successfully.');
        // Reset form
        setForm({
          first: '',
          last: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        // Close modal after 2 seconds on success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to send email');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly at team@justdevit.org');
      console.error('Email submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key and prevent background scrolling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Prevent background scrolling when modal is open
      document.body.classList.add('modal-open');
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      // Restore scrolling when modal closes
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="contact-modal-overlay"
          onClick={onClose}
        >
          <div 
            className="contact-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contact-modal-header">
              <h2 className="contact-modal-title">Get In Touch</h2>
              <button 
                className="contact-modal-close"
                onClick={onClose}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            
            <div className="contact-modal-body">
              <FloatingBackground />
              
              <div className="contact-grid">
                <div className="contact-intro">
                  <div className="contact-kicker">Have any question?</div>
                  <h3 className="contact-title">
                    Book a meeting with our experts to discuss your needs.
                  </h3>
                  <div className="contact-divider"></div>

                  <div className="contact-email-info">
                    <div className="email-details">
                      <span className="email-label">Contact us directly</span>
                      <a href="mailto:team@justdevit.org" className="email-address">
                        team@justdevit.org
                      </a>
                    </div>
                  </div>

                  <div className="contact-social">
                    <span>Find us on</span>
                    <div className="social-icons">
                      <a
                        className="social-link"
                        href="https://www.linkedin.com"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="social-icon"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        LinkedIn
                      </a>
                      <a
                        className="social-link"
                        href="https://twitter.com"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="social-icon"
                        >
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                        </svg>
                        Twitter
                      </a>
                      <a className="social-link" href="mailto:team@justdevit.org">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="social-icon"
                        >
                          <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-.904.732-1.636 1.636-1.636h.91L12 10.09l9.455-6.269h.909c.904 0 1.636.732 1.636 1.636z" />
                        </svg>
                        Email
                      </a>
                    </div>
                  </div>
                </div>

                <form
                  className="contact-form glass-morphism"
                  onSubmit={handleSubmit}
                >
                  <div className="form-row">
                    <div className="form-field">
                      <label>First</label>
                      <input
                        name="first"
                        value={form.first}
                        onChange={handleChange}
                        placeholder="First name"
                      />
                    </div>
                    <div className="form-field">
                      <label>Last</label>
                      <input
                        name="last"
                        value={form.last}
                        onChange={handleChange}
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Email</label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Phone</label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Subject</label>
                    <input
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                    />
                  </div>

                  <div className="form-field">
                    <label>Nature of Inquiry</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us a bit about your project..."
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="modern-btn primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Submit'}
                    </button>
                    {submitMessage && (
                      <div 
                        className={`submit-message ${submitStatus === 'success' ? 'success' : 'error'}`}
                        style={{
                          marginTop: 'var(--space-4)',
                          padding: 'var(--space-3)',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: submitStatus === 'success' ? 'rgba(51, 192, 176, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                          color: submitStatus === 'success' ? '#33C0B0' : '#EF4444',
                          fontSize: 'var(--font-size-sm)',
                          textAlign: 'center',
                        }}
                      >
                        {submitMessage}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
